import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  BookOpenCheck, 
  Sparkles, 
  HelpCircle, 
  LogOut, 
  ArrowLeft, 
  CheckCircle2, 
  Archive, 
  Play, 
  FileText, 
  Star,
  Flame,
  Zap,
  Calendar,
  Bookmark
} from 'lucide-react';
import axios from 'axios';
import './StyleComponents/PracticeLab.css';
import DigitalTestingLab from './DigitalTestingLab';

const PracticeLab = () => {
  const [view, setView] = useState('dashboard'); // 'dashboard', 'review', or 'testing'
  const [selectedModule, setSelectedModule] = useState('rw1');
  const [activeQuestion, setActiveQuestion] = useState(6);

  // State to hold the test ID currently being taken
  const [activeTestData, setActiveTestData] = useState(null);

  // State variables for dynamic server data
  const [testsMap, setTestsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tests from server on component mount
  useEffect(() => {
    async function fetchTests() {
      try {
        const response = await axios.get('https://tenaflyprepacademy.com/server/practicelab/get', {
          headers: { "jwt": localStorage.getItem("user") }
        });

        const test_accesses = response.data["test_accesses"]
        const test_sessions = response.data["test_sessions"]
        const digitalTests = response.data["digital_tests"]
        const completed_tests = response.data["completed_tests"]

        // console.log(digitalTests)

        // Group everything cleanly into a single map indexed by testId
        const organizedMap = {};

        test_accesses.forEach(access => {
          const id = access.digital_sat_id;
          // console.log(digitalTests.find(t=> t.test_id === id) || null)
          organizedMap[id] = {
            "digitalTest": digitalTests.find(t => t.test_id === id) || null,
            "completed": completed_tests.find(c => c.test_id === id || c.id === id) || null,
            "session": test_sessions.find(s => s.digital_sat_id === id) || null
          };
        });
        // console.log(organizedMap)
        setTestsMap(organizedMap);
        // console.log(testsMap)
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchTests();
  }, []);

  const handleLaunchTest = (testId) => {
    // Grab the packed data bundle from the map using the ID
    // console.log(testId)
    // console.log(testsMap[testId])
    const testData = testsMap[testId];
    setActiveTestData(testData);
    setView('testing');
  };

  const modules = [
    { id: 'rw1', name: 'Reading & Writing 1', icon: BookOpen },
    { id: 'rw2', name: 'Reading & Writing 2', icon: BookOpenCheck },
    { id: 'math1', name: 'Math 1', icon: Sparkles },
    { id: 'math2', name: 'Math 2', icon: Sparkles },
  ];

  const handleExit = () => {
    if (view === 'review') {
      setView('dashboard');
    } else {
      alert('Exiting application...');
    }
  };

  if (view === 'testing') {
    return (
      <DigitalTestingLab 
        test={activeTestData} 
        onExit={() => {
          setActiveTestData(null);
          setView('dashboard');
        }} 
      />
    );
  }
  
  return (
    <div className="lab-container">
      {/* Dynamic Contextual Sidebar */}
      <aside className="lab-sidebar">
        {view === 'dashboard' ? (
          <>
            <div className="sidebar-header">
              <h3 className="hub-title">Next Up Hub</h3>
              <p className="sidebar-subtitle">Your personalized daily path</p>
            </div>

            <div className="sidebar-nav action-hub-content">
              <div className="hub-card streak-card">
                <div className="hub-card-header">
                  <Flame size={18} className="text-streak" fill="currentColor" />
                  <span>5-Day Streak</span>
                </div>
                <p className="hub-card-text">Keep it up! Log in tomorrow to protect your streak.</p>
              </div>

              <div className="hub-card primary-action-card">
                <div className="hub-card-header">
                  <Zap size={18} className="text-action" fill="currentColor" />
                  <span>Recommended Drill</span>
                </div>
                <p className="hub-card-text">Strengthen your **Linear Equations** syntax with a quick focus drill.</p>
                <button className="btn-hub-primary" onClick={() => alert('Launching rapid fire drill...')}>
                  Start 5-Min Drill
                </button>
              </div>

              <button className="hub-clickable-row" onClick={() => { setView('review'); setActiveQuestion(10); }}>
                <div className="row-left">
                  <Bookmark size={16} />
                  <span>Flagged Items</span>
                </div>
                <span className="row-badge">12</span>
              </button>

              <div className="hub-card countdown-card">
                <div className="hub-card-header">
                  <Calendar size={16} />
                  <span>Official Test Day</span>
                </div>
                <p className="countdown-days"><strong>42 Days</strong> Remaining</p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="sidebar-header">
              <div className="module-badge">
                <BookOpen className="sidebar-icon-badge" size={20} />
                <div>
                  <p className="sidebar-title">Exam Modules</p>
                  <p className="sidebar-subtitle">Practice Mode</p>
                </div>
              </div>
            </div>

            <nav className="sidebar-nav">
              {modules.map((mod) => {
                const IconComponent = mod.icon;
                return (
                  <button
                    key={mod.id}
                    className={`nav-item ${selectedModule === mod.id ? 'active' : ''}`}
                    onClick={() => setSelectedModule(mod.id)}
                  >
                    <IconComponent size={18} /> {mod.name}
                  </button>
                );
              })}
            </nav>
          </>
        )}

        <div className="sidebar-footer">
          <button className="nav-item secondary">
            <HelpCircle size={18} /> Help Center
          </button>
          <button className="nav-item secondary logout" onClick={handleExit}>
            <LogOut size={18} /> 
            {view === 'review' ? 'Back to Dashboard' : 'Exit Lab'}
          </button>
        </div>
      </aside>

      {/* Conditional View Workspace */}
      {view === 'dashboard' ? (
        <main className="main-content">
          {/* Performance Summary Section */}
          <section className="stats-row">
            <div className="card performance-summary">
              <h2>Performance Summary</h2>
              <p>Your progress across the last 30 days of active prep.</p>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">AVERAGE SCORE</span>
                  <span className="stat-value">1380</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">TESTS AVAILABLE</span>
                  <span className="stat-value">
                    {loading ? "..." : Object.keys(testsMap).length}
                  </span>
                </div>
              </div>
            </div>

            <div className="card score-target">
              <h2>Score Target</h2>
              <p>You are 120 points away from your goal of 1500.</p>
              <div className="progress-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '85%' }}></div>
                </div>
                <div className="progress-labels">
                  <span>Current: 1380</span>
                  <span>Target: 1500</span>
                </div>
              </div>
            </div>
          </section>

          {/* Hero CTA */}
          <section className="hero-cta card">
            <div className="hero-text">
              <h3>Ready for a new challenge?</h3>
              <p>Simulate a full-length digital SAT experience with our latest practice module designed by top-tier educators.</p>
              <button className="btn-primary">Start New Practice Test</button>
            </div>
            <div className="hero-image">
              <img src="https://images.unsplash.com/photo-1544652478-6653e09f18a2?auto=format&fit=crop&q=80&w=800" alt="Tablet with digital test" />
            </div>
          </section>

          {/* Dynamic Practice Test List */}
          <section className="test-list-section">
            <div className="list-header">
              <h3>Your Practice Tests</h3>
              <button className="link-btn">View Archived Tests</button>
            </div>
            
            <div className="test-list">
              {loading && <p>Loading practice tests...</p>}
              {error && <p className="text-danger">Error loading tests: {error}</p>}
              
              {!loading && !error && Object.keys(testsMap).length === 0 && (
                <p>No practice tests available at the moment.</p>
              )}

              {!loading && !error && Object.entries(testsMap).map(([testId, testItem], i) => {
                const { completed, session } = testItem;

                return (
                  <div key={testId} className="test-card card">
                    <div className="test-info">
                      <div className="test-icon">
                        <CheckCircle2 size={20} className="text-success" />
                      </div>
                      <div>
                        <h4>Digital SAT #{i + 1}</h4>
                      </div>
                    </div>
                    
                    <div className="test-score">
                      <span className="score-label">STATUS</span>
                      <span className="score-value" style={{ fontSize: '1rem', marginTop: '4px' }}>
                        {completed ? "Completed" : (session && !session.isFinished) ? "In Progress" : "Not Started"}
                      </span>
                    </div>
                    
                    <div className="test-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {completed ? (
                        <>
                          <button 
                            className="btn-primary-review" 
                            onClick={() => setView('review')}
                          >
                            Launch Self Review
                          </button>
                          <button 
                            className="btn-secondary-retake" 
                            onClick={() => handleLaunchTest(testId)}
                          >
                            Retake Test
                          </button>
                        </>
                      ) : (
                        <button 
                          className="btn-secondary" 
                          onClick={() => handleLaunchTest(testId)}
                        >
                          {session && !session.isFinished ? 'Continue Taking Test' : 'Start Test'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </main>
      ) : (
        /* Workspace Review View */
        <main className="workspace">
          <header className="workspace-header">
            <div className="workspace-nav-header">
              <button className="link-btn back-to-dash" onClick={() => setView('dashboard')}>
                <ArrowLeft size={16} /> Back to Dashboard
              </button>
              <h3>Reviewing Practice Test</h3>
            </div>
            <div className="question-grid">
              {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  className={`grid-item ${num === activeQuestion ? 'active' : ''} ${
                    [2, 5, 10, 19, 23, 27, 30].includes(num) ? 'incorrect' : 'correct'
                  }`}
                  onClick={() => setActiveQuestion(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </header>

          <div className="split-view">
            <section className="content-panel question-panel">
              <div className="card question-card">
                <img
                  src="https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?auto=format&fit=crop&q=80&w=1200"
                  alt="SAT Question"
                  className="question-image"
                />
              </div>

              <div className="results-feedback">
                <div className="result-card incorrect">
                  <div className="icon-box-lucide">
                    <span className="feedback-cross">✕</span>
                  </div>
                  <div className="result-text">
                    <span className="label">YOUR ANSWER</span>
                    <span className="value">B (Selected)</span>
                  </div>
                </div>

                <div className="result-card correct">
                  <div className="icon-box-lucide">
                    <span className="feedback-check">✓</span>
                  </div>
                  <div className="result-text">
                    <span className="label">CORRECT ANSWER</span>
                    <span className="value">D (Correct)</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="content-panel video-panel">
              <div className="video-section">
                <div className="section-header">
                  <Play size={18} />
                  <h3>Video Explanation</h3>
                </div>
                <div className="video-container">
                  <div className="video-placeholder">
                    <img src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&q=80&w=800" alt="Video thumbnail" />
                    <div className="play-overlay">
                      <Play size={24} fill="currentColor" />
                    </div>
                  </div>
                  <div className="video-info">
                    <p className="video-title">Module {selectedModule.replace(/^\D+/g, '') || '1'}: Question {activeQuestion} Analysis</p>
                    <p className="video-meta">8:42 mins • Expert Instructor</p>
                  </div>
                </div>
              </div>

              <div className="notes-section card">
                <div className="section-header">
                  <FileText size={18} />
                  <h3>Instructor Notes</h3>
                </div>
                <p className="notes-content">
                  Focus on the specific shift in tone between lines 14-22. The author moves from observational data to hypothetical cluster formations.
                </p>
              </div>

              <div className="tags-section">
                <span className="tag">Galaxy Clusters</span>
                <span className="tag">Scientific Inquiry</span>
                <span className="tag">Data Interpretation</span>
              </div>

              <button className="save-btn">
                <Star size={16} /> Save for Late Review
              </button>
            </section>
          </div>
        </main>
      )}
    </div>
  );
};

export default PracticeLab;